import { useState } from 'react';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { Button, MoreOptions, Pagination, User } from 'components/common';
import {
  Box,
  BoxEmpty,
  BoxTable,
  BoxTableCell,
  BoxTableHeader,
  BoxTableRow
} from 'components/common/box';
import {
  Modal,
  ModalContent,
  ModalFooter,
  ModalPortal
} from 'components/common/modal';
import {
  CourseUserDeadline,
  CourseUserMandatory
} from 'components/courses/common';
import { ShareUsersTargetSchedule } from '.';

import type { Group } from 'shared-types';
import type { GetUsersInGroupsRes } from 'shared-types/responses';
import type { ApiDataHookUpdateHandler } from 'lib/api/useApiData';

interface Props {
  groups: Pick<Group, 'id'>[];
  updateShareUsers: ApiDataHookUpdateHandler;
}

type State = Pick<GetUsersInGroupsRes, 'page' | 'pages' | 'users'>;

const ShareGroupsUsers = ({ groups, updateShareUsers }: Props) => {
  const { dispatch } = useDispatch();

  const {
    messages,
    pageState: { course }
  } = useReduxState();

  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [state, setState] = useState<State>({
    page: 1,
    pages: 0,
    users: []
  });
  const [userDataUpdated, setUserDataUpdated] = useState(false);

  const hideModal = () => {
    setModalVisible(false);
    if (userDataUpdated) updateShareUsers({});
  };

  const showModal = () => {
    setModalVisible(true);
    setUserDataUpdated(false);
    updateUsers({ page: 1, onShow: true });
  };

  const updateUsers = async ({ page, onShow = false }) => {
    try {
      setLoading(true);

      const pageValue = page || state.page;
      const users = await api.get<GetUsersInGroupsRes>(
        `/admin/courses/${course.id}/group-users` +
          (pageValue > 1 ? `?page=${pageValue}` : '')
      );

      setState({
        ...state,
        users: users.data.users,
        page: pageValue,
        pages: users.data.pages
      });

      if (!onShow) setUserDataUpdated(true);
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        disabled={!groups || !groups.length}
        onClick={showModal}
        size="header"
        style={{ marginLeft: 10 }}
        text={messages.commonViewUsers}
      />
      <ModalPortal>
        <Modal
          closeButton={true}
          onHide={hideModal}
          title={messages.settingsGroupsUsers}
          visible={modalVisible}
          width="wide"
        >
          <ModalContent style={{ padding: 0 }}>
            <Box loading={loading} padding={0}>
              {state.users.length ? (
                <BoxTable>
                  <BoxTableHeader>
                    <BoxTableCell>{messages.commonUser}</BoxTableCell>
                    <BoxTableCell>{messages.commonMandatory}</BoxTableCell>
                    <BoxTableCell>{messages.courseTargetSchedule}</BoxTableCell>
                    <BoxTableCell>{messages.commonGroups}</BoxTableCell>
                    <BoxTableCell></BoxTableCell>
                  </BoxTableHeader>

                  {state.users.map((user) => (
                    <BoxTableRow key={user.id}>
                      <BoxTableCell>
                        <User enableProfileLink={true} user={user} />
                      </BoxTableCell>

                      <BoxTableCell>
                        <CourseUserMandatory
                          mandatoryReasons={user.mandatoryReasons}
                        />
                      </BoxTableCell>

                      <BoxTableCell>
                        <CourseUserDeadline
                          deadline={user.deadline}
                          isEdited={Boolean(user.courseSettings?.deadlineType)}
                          noStatus={true}
                        />
                      </BoxTableCell>

                      <BoxTableCell>
                        {user.groups.map((group) => group.title).join(', ')}
                      </BoxTableCell>

                      <BoxTableCell shrink={true}>
                        <MoreOptions renderAsFixed={true}>
                          <ShareUsersTargetSchedule
                            course={course}
                            mandatoryReasons={user.mandatoryReasons}
                            updateUsers={updateUsers}
                            userCourseSettings={user.courseSettings}
                            userId={user.id}
                          />
                        </MoreOptions>
                      </BoxTableCell>
                    </BoxTableRow>
                  ))}
                </BoxTable>
              ) : (
                <BoxEmpty text={messages.commonNoUsers} />
              )}
            </Box>
          </ModalContent>

          {state.users.length ? (
            <ModalFooter>
              <Pagination
                currentPage={state.page}
                numberOfPages={state.pages}
                onPageChange={updateUsers}
                style={{ marginTop: 0 }}
              />
            </ModalFooter>
          ) : null}
        </Modal>
      </ModalPortal>
    </>
  );
};

export default ShareGroupsUsers;
